import {
  addStopPlace,
  findSpots,
  getHere,
  getNearby,
  getPlan,
  NotFound,
  readPasted,
  updateStop,
  type Here,
  type NearThing,
  type Plan,
  type PlanStop,
  type Reading,
  type Spot,
} from '@trippath/shared';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { MapPanel } from '../../components/map/MapPanel';
import type { MapData } from '../../lib/map';
import { stopInputOf } from '../../lib/stopInput';

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; plan: Plan; stop: PlanStop };

/** A point the map was held on: where on the map, and what is there once said. */
interface Held {
  lat: number;
  lon: number;
  x: number;
  y: number;
  here: Here | null;
  /** The named things near it, once said; none when OpenStreetMap could not say. */
  near: NearThing[] | null;
}

/** What the paste field reads besides a link, as the mockup lists it: each with how it is read. */
const FORMATS: [string, string][] = [
  ['35.0393, 135.7292', 'decimal'],
  ['35°02\'21.5"N 135°43\'45.1"E', 'seconds'],
  ["N 35° 2.358' E 135° 43.752'", 'minutes'],
  ['8Q7Q2PQH+QM', 'plusCode'],
  ['2PQH+QM Kyoto', 'shortCode'],
];

/** A point as the page says it: `35,0393, 135,7292` in Estonian. */
function coordinates(lat: number, lon: number, language: string): string {
  const one = (value: number) =>
    value.toLocaleString(language, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  return `${one(lat)}, ${one(lon)}`;
}

/**
 * A place added to a stop from the map (the `MapAdd` mockup): searched by name near the stop, or
 * pasted as a Google Maps link, coordinates or a Plus Code; or the map held on a point, what is
 * there and near it shown, each added at once, or the stop itself put there.
 */
export function MapAdd() {
  const { t } = useTranslation();
  const { id = '', stop: stopId = '' } = useParams();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let live = true;
    getPlan(id).then(
      (plan) => {
        if (!live) return;
        const stop = plan.stops.find((one) => one.id === stopId);
        setState(stop === undefined ? { kind: 'missing' } : { kind: 'ready', plan, stop });
      },
      (error: unknown) => {
        if (live) setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [id, stopId]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p className="form-failed" role="alert">
          {t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}
        </p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  return (
    <Adding
      trip={id}
      stop={state.stop}
      onPlan={(plan) => {
        const stop = plan.stops.find((one) => one.id === stopId);
        if (stop) setState({ kind: 'ready', plan, stop });
      }}
    />
  );
}

/** The stop's place added: by search, by paste, or from the map. */
function Adding({
  trip,
  stop,
  onPlan,
}: {
  trip: string;
  stop: PlanStop;
  onPlan: (plan: Plan) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const city = stop.city;
  const centre = useMemo(
    () =>
      stop.place === null ? { lat: 0, lon: 0 } : { lat: stop.place.lat, lon: stop.place.lon },
    [stop.place],
  );
  const [tab, setTab] = useState<'search' | 'paste'>('paste');
  const [typed, setTyped] = useState('');
  const [spots, setSpots] = useState<Spot[] | null>(null);
  const [pasted, setPasted] = useState('');
  const [reading, setReading] = useState<Reading | 'unread' | null>(null);
  const [held, setHeld] = useState<Held | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  // Searched by name near the stop, as it is typed.
  useEffect(() => {
    if (tab !== 'search' || typed.trim().length < 2) return;
    const stopped = new AbortController();
    const asked = setTimeout(() => {
      findSpots(typed.trim(), centre, stopped.signal).then(
        (found) => {
          setSpots(found.spots);
        },
        () => undefined,
      );
    }, 300);
    return () => {
      clearTimeout(asked);
      stopped.abort();
    };
  }, [tab, typed, centre]);

  // Read as it is pasted: a link, coordinates or a Plus Code; else searched by name.
  useEffect(() => {
    if (tab !== 'paste' || pasted.trim() === '') return;
    const stopped = new AbortController();
    const asked = setTimeout(() => {
      readPasted(pasted.trim(), centre, stopped.signal).then(
        (found) => {
          setReading(found ?? 'unread');
        },
        () => undefined,
      );
    }, 300);
    return () => {
      clearTimeout(asked);
      stopped.abort();
    };
  }, [tab, pasted, centre]);

  const add = (
    name: string,
    address: string | null,
    lat: number,
    lon: number,
    source: 'search' | 'map' | 'google',
  ) => {
    setFailed(false);
    addStopPlace(trip, stop.id, { name, address, lat, lon, source }).then(
      () => {
        setSaid(t('mapAdd.added', { city, name }));
        setHeld(null);
      },
      () => {
        setFailed(true);
      },
    );
  };

  const hold = (point: { lat: number; lon: number; x: number; y: number }) => {
    setHeld({ ...point, here: null, near: null });
    // Each answer kept only while the point it is of is still the one held.
    const answered = (change: Partial<Held>) => {
      setHeld((was) =>
        was !== null && was.lat === point.lat && was.lon === point.lon
          ? { ...was, ...change }
          : was,
      );
    };
    getHere(point).then(
      (here) => {
        answered({ here });
      },
      () => {
        setFailed(true);
      },
    );
    getNearby(point).then(
      (found) => {
        answered({ near: found.near });
      },
      () => {
        answered({ near: [] });
      },
    );
  };

  const moveStop = (here: Here) => {
    setFailed(false);
    const name = here.address ?? coordinates(here.lat, here.lon, lang);
    updateStop(trip, stop.id, {
      ...stopInputOf(stop),
      place: { lat: here.lat, lon: here.lon, name },
    }).then(
      (plan) => {
        setHeld(null);
        setSaid(t('mapAdd.moved', { city, address: name }));
        onPlan(plan);
      },
      () => {
        setFailed(true);
      },
    );
  };

  /** What may be done with the point held, once what is there is said. */
  const pointActions = (here: Here) => (
    <>
      <button
        type="button"
        className="button button-primary button-small"
        onClick={() => {
          add(here.address ?? t('mapAdd.point'), here.address, here.lat, here.lon, 'map');
        }}
      >
        {t('mapAdd.addPoint', { city })}
      </button>
      <button
        type="button"
        className="button button-small"
        onClick={() => {
          moveStop(here);
        }}
      >
        {t('mapAdd.moveStop')}
      </button>
    </>
  );

  const read = reading !== null && reading !== 'unread' ? reading : null;
  const data: MapData = {
    points:
      stop.place === null
        ? []
        : [{ lat: centre.lat, lon: centre.lon, label: city, kind: 'stop' }],
    lines: [],
  };
  const focus = held ?? (read ? { lat: read.lat, lon: read.lon } : null);

  return (
    <div className="mapadd-page">
      <aside className="mapadd-left" aria-label={t('mapAdd.title', { city })}>
        <div className="mapadd-sec">
          <h1>{t('mapAdd.title', { city })}</h1>
          <div className="mapadd-seg" role="tablist" aria-label={t('mapAdd.how')}>
            {(['search', 'paste'] as const).map((one) => (
              <button
                key={one}
                type="button"
                role="tab"
                aria-selected={tab === one}
                onClick={() => {
                  setTab(one);
                }}
              >
                {t(`mapAdd.${one}`)}
              </button>
            ))}
          </div>
          {tab === 'search' ? (
            <>
              <label className="field">
                <span className="field-label">{t('mapAdd.searchLabel')}</span>
                <input
                  className="text-input"
                  type="search"
                  value={typed}
                  onChange={(event) => {
                    setTyped(event.target.value);
                    setSpots(null);
                  }}
                />
              </label>
              {spots !== null &&
                (spots.length === 0 ? (
                  <p className="quiet mapadd-small">{t('mapAdd.noSpots', { city })}</p>
                ) : (
                  <ul className="mapadd-spots">
                    {spots.map((spot) => (
                      <li key={`${spot.name}-${String(spot.lat)}-${String(spot.lon)}`}>
                        <span>
                          <b>{spot.name}</b>
                          <small className="quiet">{spot.detail}</small>
                        </span>
                        <button
                          type="button"
                          className="link-button"
                          aria-label={`${t('mapAdd.add')}: ${spot.name}`}
                          onClick={() => {
                            add(spot.name, spot.detail || null, spot.lat, spot.lon, 'search');
                          }}
                        >
                          {t('mapAdd.add')}
                        </button>
                      </li>
                    ))}
                  </ul>
                ))}
            </>
          ) : (
            <>
              <label className="field">
                <span className="field-label">{t('mapAdd.pasteLabel')}</span>
                <input
                  className="text-input mono"
                  type="text"
                  value={pasted}
                  onChange={(event) => {
                    setPasted(event.target.value);
                    setReading(null);
                  }}
                />
              </label>
              {reading === 'unread' && (
                <p className="quiet mapadd-small" role="status">
                  {t('mapAdd.unread')}{' '}
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setTyped(pasted);
                      setTab('search');
                    }}
                  >
                    {t('mapAdd.search')}
                  </button>
                </p>
              )}
              {read !== null && (
                <div className="mapadd-read">
                  <span className="mapadd-src">{pasted.trim()}</span>
                  <div className="mapadd-out">
                    <span>
                      <b>
                        {read.name ?? read.address ?? coordinates(read.lat, read.lon, lang)}
                      </b>
                      <br />
                      <span className="mapadd-small quiet">
                        {[read.address, coordinates(read.lat, read.lon, lang)]
                          .filter((one) => one !== null)
                          .join(' · ')}
                      </span>
                    </span>
                    <span className="tag tag-ok">{t(`mapAdd.readAs.${read.kind}`)}</span>
                  </div>
                  <span className="mapadd-small quiet">{t('mapAdd.readHow')}</span>
                  <div className="mapadd-row">
                    <button
                      type="button"
                      className="button button-quiet button-small"
                      onClick={() => {
                        setPasted('');
                        setReading(null);
                      }}
                    >
                      {t('mapAdd.clear')}
                    </button>
                    <button
                      type="button"
                      className="button button-primary button-small"
                      onClick={() => {
                        add(
                          read.name ?? read.address ?? t('mapAdd.point'),
                          read.address,
                          read.lat,
                          read.lon,
                          read.kind === 'link' ? 'google' : 'map',
                        );
                      }}
                    >
                      {t('mapAdd.addTo', { city })}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          {said !== null && (
            <p className="mapadd-said" role="status">
              {said} <Link to={`/trips/${trip}/stops/${stop.id}`}>{t('mapAdd.toDays')}</Link>
            </p>
          )}
          {failed && (
            <p className="form-failed" role="alert">
              {t('mapAdd.failed')}
            </p>
          )}
        </div>
        <div className="mapadd-sec">
          <span className="mapadd-caps">{t('mapAdd.formats')}</span>
          <div className="mapadd-fmt">
            {FORMATS.map(([example, kind]) => (
              <span key={kind} className="mapadd-fmt-row">
                <span className="mono">{example}</span>
                <span className={`tag ${kind === 'shortCode' ? 'tag-info' : 'tag-ok'}`}>
                  {t(`mapAdd.readAs.${kind}`)}
                </span>
              </span>
            ))}
            <span className="mapadd-fmt-row">
              <span className="mono">google.com/maps/place/…</span>
              <span className="tag tag-ok">{t('mapAdd.longLink')}</span>
            </span>
          </div>
          <span className="mapadd-small quiet">{t('mapAdd.nameOnly')}</span>
        </div>
        <div className="mapadd-sec">
          <span className="mapadd-caps">{t('mapAdd.hint')}</span>
          <span className="mapadd-small">{t('mapAdd.hintText')}</span>
        </div>
      </aside>
      <div className="mapadd-map">
        <MapPanel
          data={data}
          label={t('mapAdd.mapLabel', { city })}
          cooperative={false}
          closest={14}
          focus={focus}
          controls
          // Pressed, held or right-clicked: what is there, to add at once.
          onSpot={hold}
          onHold={hold}
          // Its box goes with the point as the map moves.
          follow={
            held && {
              lat: held.lat,
              lon: held.lon,
              onMove: (at) => {
                setHeld((was) => was && { ...was, ...at });
              },
            }
          }
        />
        {held !== null && (
          <section
            className="mapadd-pop"
            style={{ left: held.x, top: held.y }}
            aria-labelledby="mapadd-here"
          >
            <div className="mapadd-pop-head">
              <span className="mapadd-caps">{t('mapAdd.here')}</span>
              <b id="mapadd-here">
                {held.here?.address ?? coordinates(held.lat, held.lon, lang)}
              </b>
              <span className="mapadd-small quiet mono">
                {coordinates(held.lat, held.lon, lang)}
                {held.here !== null && ` · Plus Code ${held.here.plusCode}`}
              </span>
            </div>
            {held.near === null ? (
              <p className="mapadd-small quiet mapadd-pop-wait">{t('myPlaces.asking')}</p>
            ) : (
              held.near.length > 0 && (
                <ul className="mapadd-near" aria-label={t('mapAdd.near')}>
                  {held.near.map((near) => (
                    <li key={`${near.name}-${String(near.metres)}`}>
                      <span>
                        {near.emoji} {near.name}
                      </span>
                      <span className="mapadd-small quiet mono">{near.metres} m</span>
                      <button
                        type="button"
                        className="link-button"
                        aria-label={`${t('mapAdd.add')}: ${near.name}`}
                        onClick={() => {
                          add(near.name, null, near.lat, near.lon, 'map');
                        }}
                      >
                        {t('mapAdd.add')}
                      </button>
                    </li>
                  ))}
                </ul>
              )
            )}
            <div className="mapadd-pop-foot">
              {held.here !== null && pointActions(held.here)}
              <button
                type="button"
                className="button button-quiet button-small"
                onClick={() => {
                  setHeld(null);
                }}
              >
                {t('mapAdd.close')}
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
